import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const theme = {
  primaryDark: "#1b325f", primaryBlue: "#282f81", accentCyan: "#11abea",
  bgLight: "#f8fafc", cardBg: "#ffffff", textMain: "#334155",
  textMuted: "#64748b", borderLight: "#e2e8f0", tableBorder: "#caced5"
};

const StockTransfer = ({ screenAction, triggerSave }) => {
  const today = new Date().toISOString().split('T')[0];

  const initHeader = {
    issue_id: "", issue_dt: today, issued_to: ""
  };

  const initLine = {
    medicine_id: "", medicine_name: "", unit: "Box", batch_no: "", 
    issued_qty: "", expiry_date: ""
  };

  const [header, setHeader] = useState(initHeader);
  const [lines, setLines] = useState([initLine]);
  
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const [medicinesList, setMedicinesList] = useState([]);
  const [locationsList, setLocationsList] = useState([]);

  // --- ERP COMPACT STYLES ---
  const compactRowStyle = { display: 'flex', alignItems: 'center', marginBottom: '6px' };
  const compactLabelStyle = { width: '90px', fontSize: '12px', color: theme.textMain, fontWeight: '600' };
  const compactInputStyle = { 
    flex: 1, padding: '4px 6px', fontSize: '12px', border: 'none', 
    borderBottom: `1px solid ${theme.borderLight}`, outline: 'none', 
    backgroundColor: isDisabled ? 'transparent' : '#fcfcfc' 
  };

  const cellStyle = { border: `1px solid ${theme.tableBorder}`, padding: '0', margin: '0' };
  const cellInputStyle = { width: '100%', boxSizing: 'border-box', border: 'none', padding: '6px 4px', fontSize: '11px', backgroundColor: 'transparent', outline: 'none' };
  const readOnlyCell = { ...cellInputStyle, backgroundColor: '#f1f5f9', fontWeight: '600', color: theme.textMain };

  // --- DATA FETCHING ---
  // const fetchBaseData = async () => {
  //   const user = JSON.parse(localStorage.getItem("user"));
  //   try {
  //     const medRes = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/medicines/all?employee_id=${user.id}`);
  //     const medData = await medRes.json();
  //     setMedicinesList(Array.isArray(medData) ? medData : []);
  //   } catch (e) { console.error("Error loading data", e); }
  // };

  // --- DATA FETCHING ---
  const fetchBaseData = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    try {
      const [medRes, locRes] = await Promise.all([
        fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/medicines/all?employee_id=${user.id}`),
        fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/locations/all?employee_id=${user.id}`) // 🔥 Fetch locations
      ]);
      const medData = await medRes.json();
      const locData = await locRes.json();
      
      setMedicinesList(Array.isArray(medData) ? medData : []);
      setLocationsList(Array.isArray(locData) ? locData : []); // 🔥 Set locations
    } catch (e) { console.error("Error loading data", e); }
  };

  const fetchNextId = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/stock-issue/id?employee_id=${user.id}`);
    const data = await res.json();
    setHeader(prev => ({ ...prev, issue_id: data.issue_id }));
  };

  useEffect(() => { fetchBaseData(); fetchNextId(); }, []);

  const handleHeaderChange = (e) => setHeader({ ...header, [e.target.name]: e.target.value });

  const handleLineChange = (index, field, value) => {
    const updated = [...lines];
    updated[index][field] = value;

    if (field === "medicine_id") {
      const med = medicinesList.find(m => m.medicine_id === value);
      if (med) updated[index].medicine_name = med.medicine_name;
    }

    setLines(updated);
  };

  const addRow = () => setLines([...lines, { ...initLine }]);
  const removeRow = (index) => setLines(lines.filter((_, i) => i !== index));

  // --- SEARCH & MODES ---
  const searchIssue = async (text) => {
    setSearchText(text);
    if (text.length < 3) { setSearchResults([]); setShowDropdown(false); return; }
    const user = JSON.parse(localStorage.getItem("user"));
    const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/stock-issue/search?q=${text}&employee_id=${user.id}`);
    setSearchResults(await res.json());
    setShowDropdown(true);
  };

  const selectIssue = async (issue) => {
    setSearchText(`${issue.issue_id} | ${issue.issued_to}`);
    setShowDropdown(false);
    
    const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/stock-issue/${issue.issue_id}`);
    const data = await res.json();
    
    if(data.header) {
      setHeader({ 
        ...data.header, 
        issue_dt: data.header.issue_dt ? data.header.issue_dt.split('T')[0] : "" 
      });
      setLines(data.details.length > 0 ? data.details.map(d => ({
        ...d, 
        expiry_date: d.expiry_date ? d.expiry_date.split('T')[0]:""
      })) : [initLine]);
    }
    
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  const clearForm = async () => {
    setHeader(initHeader); setLines([initLine]); setSearchText("");
    await fetchNextId();
  };

  useEffect(() => {
    if (screenAction === "add" || screenAction === "refresh") { clearForm(); setIsDisabled(false); }
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
      if (screenAction === "print") printForm();


  }, [screenAction]);

  useEffect(() => { if (triggerSave > 0 && screenAction === "save") handleSubmit(); }, [triggerSave]);

  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!header.issued_to) return alert("Please select a department to issue to.");
    
    const validLines = lines.filter(l => l.medicine_id !== "");
    if (validLines.length === 0) return alert("Add at least one medicine item.");

    const payload = { header: { ...header, created_by: user.id }, details: validLines, isEdit: !!header.created_at };
    
    try {
      const res = await fetch("https://hmsapi.emedhatechnologies.com/master/api/api/stock-issue", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload)
      });
      if (res.ok) {
        alert("Stock Transfer Saved!");
        if (!payload.isEdit) clearForm();
      } else alert("Error saving Transfer");
    } catch (err) { alert("Network Error"); }
  };

const printForm = () => {
    if (!header.issue_id) return;
    const newWindow = window.open("", "", "width=900,height=700");
    
    const htmlContent = `
      <html><head><title>Stock Issue - ${header.issue_id}</title>
      <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; font-size: 13px; color: #333; }
        .title-box { display: flex; justify-content: space-between; border-bottom: 2px solid #282f81; padding-bottom: 10px; margin-bottom: 20px; }
        .title-box h1 { margin: 0; color: #282f81; font-size: 24px; text-transform: uppercase; }
        .title-box h2 { margin: 0; color: #11abea; font-size: 18px; }
        .info-grid { display: flex; gap: 40px; margin-bottom: 20px; padding: 15px; background-color: #f8fafc; border-left: 4px solid #282f81; border-radius: 4px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 12px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f4f4; color: #282f81; }
        .text-center { text-align: center; }
        .highlight { font-weight: bold; color: #282f81; text-align: right; }
      </style></head>
      <body>
        <div class="title-box">
          <div><h1>EMEDHA CARE HOSPITAL</h1><p>Internal Stock Transfer (Issue Note)</p></div>
          <div style="text-align:right"><h2>${header.issue_id}</h2><p>Date: ${header.issue_dt}</p></div>
        </div>
        
        <div class="info-grid">
          <div><strong>Issued To Department:</strong><br/><span style="font-size:16px; color:#1b325f; font-weight:bold;">${header.issued_to}</span></div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th class="text-center">SI</th>
              <th>Medicine ID</th>
              <th>Medicine Name</th>
              <th class="text-center">Unit</th>
              <th>Batch No</th>
              <th style="text-align: right;">Issued Qty</th>
              <th>Expiry Dt</th>
            </tr>
          </thead>
          <tbody>
            ${lines.map((l, i) => `
              <tr>
                <td class="text-center">${i + 1}</td>
                <td>${l.medicine_id}</td>
                <td>${l.medicine_name}</td>
                <td class="text-center">${l.unit}</td>
                <td>${l.batch_no}</td>
                <td class="highlight">${l.issued_qty}</td>
                <td>${l.expiry_date}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div style="margin-top: 60px; display: flex; justify-content: space-between; width: 80%;">
          <div style="border-top: 1px solid #000; width: 180px; text-align:center; padding-top: 5px;">Issued By (Main Store)</div>
          <div style="border-top: 1px solid #000; width: 180px; text-align:center; padding-top: 5px;">Received By (${header.issued_to})</div>
        </div>
      </body></html>
    `;
    newWindow.document.write(htmlContent); 
    newWindow.document.close();
    setTimeout(() => { newWindow.print(); newWindow.close(); }, 500);
  };

  
  return (
    <div className="registration-container fade-in" style={{ backgroundColor: theme.bgLight, padding: '10px 20px', minHeight: '100vh' }}>
      
      {(screenAction === "edit" || screenAction === "view") && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
          <div style={{ position: 'relative', width: '350px' }}>
            <input type="text" value={searchText} placeholder="Search Issue ID or Dept..." onChange={(e) => searchIssue(e.target.value)}
              style={{ width: '100%', padding: '8px 16px', borderRadius: '4px', border: `1px solid ${theme.borderLight}`, outline: 'none', fontSize: '13px' }} />
            {showDropdown && searchResults.length > 0 && (
              <div style={{ position: 'absolute', top: '105%', left: '0', width: '100%', background: theme.cardBg, border: `1px solid ${theme.borderLight}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '200px', overflowY: 'auto' }}>
                {searchResults.map(p => (
                  <div key={p.issue_id} onClick={() => selectIssue(p)} style={{ padding: '8px 12px', borderBottom: `1px solid ${theme.borderLight}`, cursor: 'pointer', fontSize: '12px' }}>
                    <strong style={{ color: theme.primaryDark }}>{p.issue_id}</strong> &nbsp; | &nbsp; {p.issued_to}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ backgroundColor: '#fff', border: `1px solid ${theme.borderLight}`, borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', padding: '20px' }}>
        
        {/* HEADER SECTION */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '40px', marginBottom: '20px', paddingBottom: '15px', borderBottom: `2px solid ${theme.primaryDark}` }}>
          <div>
            <div style={compactRowStyle}>
              <div style={compactLabelStyle}>Issue ID:</div>
              <input type="text" name="issue_id" value={header.issue_id} disabled style={{...compactInputStyle, fontWeight: 'bold', color: theme.primaryBlue}} />
            </div>
            <div style={compactRowStyle}>
              <div style={compactLabelStyle}>Issue Date:</div>
              <input type="date" name="issue_dt" value={header.issue_dt} onChange={handleHeaderChange} disabled={isDisabled} style={compactInputStyle} />
            </div>
          </div>

          <div>
<div style={compactRowStyle}>
              <div style={compactLabelStyle}>Issued To:</div>
              <select name="issued_to" value={header.issued_to} onChange={handleHeaderChange} disabled={isDisabled} style={compactInputStyle}>
                <option value="">Select Department...</option>
                {locationsList.map((loc, i) => (
                  <option key={i} value={loc.location_name}>{loc.location_name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* LINE ITEMS DATA GRID */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '15px', color: theme.primaryDark }}>Stock Items to Transfer</h3>
          {!isDisabled && <button type="button" onClick={addRow} style={{ background: theme.primaryBlue, color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>+ Add Row</button>}
        </div>
        
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: `1px solid ${theme.tableBorder}` }}>
          <table style={{ width: '100%', minWidth: '900px', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead style={{ backgroundColor: theme.primaryDark, color: '#fff' }}> 
              <tr>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '40px', textAlign: 'center' }}>SI</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '120px' }}>Medicine ID</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '220px' }}>Medicine Name</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '80px', textAlign: 'center' }}>Unit</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '120px' }}>Batch No</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '80px', textAlign: 'right' }}>Issued Qty</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '120px' }}>Expiry Dt</th>
                {!isDisabled && <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '40px' }}></th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => (
                <tr key={idx} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                  <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8fafc' }}>{idx + 1}</td>
                  
                  <td style={cellStyle}>
                    <select value={line.medicine_id} onChange={(e) => handleLineChange(idx, 'medicine_id', e.target.value)} disabled={isDisabled} style={cellInputStyle}>
                      <option value=""></option>
                      {medicinesList.map(m => <option key={m.medicine_id} value={m.medicine_id}>{m.medicine_id}</option>)}
                    </select>
                  </td>
                  
                  <td style={cellStyle}><input type="text" value={line.medicine_name} disabled style={readOnlyCell} title={line.medicine_name} /></td>
                  
                  <td style={cellStyle}>
                    <select value={line.unit} onChange={(e) => handleLineChange(idx, 'unit', e.target.value)} disabled={isDisabled} style={{...cellInputStyle, textAlign: 'center'}}>
                      <option>Box</option><option>Strip</option><option>Loose</option>
                    </select>
                  </td>
                  
                  <td style={cellStyle}><input type="text" value={line.batch_no} onChange={(e) => handleLineChange(idx, 'batch_no', e.target.value)} disabled={isDisabled} style={cellInputStyle} placeholder="Enter Batch" /></td>
                  
                  <td style={cellStyle}><input type="number" value={line.issued_qty} onChange={(e) => handleLineChange(idx, 'issued_qty', e.target.value)} disabled={isDisabled} style={{...cellInputStyle, textAlign: 'right', fontWeight: 'bold'}} /></td>
                  
                  <td style={cellStyle}><input type="date" value={line.expiry_date} onChange={(e) => handleLineChange(idx, 'expiry_date', e.target.value)} disabled={isDisabled} style={cellInputStyle} /></td>
                  
                  {!isDisabled && <td style={{ ...cellStyle, textAlign: 'center', backgroundColor: '#fff' }}><button type="button" onClick={() => removeRow(idx)} style={{ color: 'red', border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}>X</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default StockTransfer;